import api from '@shared/api/apiConfig';
import {
  createAndSaveReportUri,
  deleteReportByIdUri,
  downloadReportByIdUri,
  downloadTempReportUri,
  editAndSaveReportUri,
  getAllReportsUri,
  getDashboardReportsURI
} from '@shared/api/endpoints';
import type { DashboardFilters, DashboardResponse } from '@shared/api/types';

export interface IReportsResponse {
  reportId: string
  reportName: string
  dpId: string
  spId: string
  dimensions: string
  metrics: string
  generatedAt: string
  updatedAt: string
  isActive: number
  appId: string
  adUnitType: string
  country: string
  platform: string
  startDate: string
  endDate: string
  userId: string
  url: string
}

export interface ISaveReportRequest {
  reportName: string
  dpId: string
  spId: string
  dimensions: string
  metrics: string
  appId: string
  adUnitType: string
  country: string
  platform: string
  startDate: string
  endDate: string
}


const formatDate = (date: string | Date) => {
  const value = new Date(date);
  return `${value.getFullYear()}-${(value.getMonth() + 1).toString().padStart(2, '0')}-${value
    .getDate()
    .toString()
    .padStart(2, '0')}`;
};

const metricMapping = new Map<string, string>([
  ['Total', 'total'],
  ['App Name', 'app_id'],
  ['Country', 'country'],
  ['Ad Unit Type', 'ad_unit_type'],
  ['Platform', 'platform'],
  // ['Org ID', 'dp_id'],
  ['DP ID', 'dp_id'],
  ['SP ID', 'sp_id']
]);

function buildDashboardQuery(filters: DashboardFilters & { activeMetric: string }) {
  return getDashboardReportsURI({
    formattedStartDate: formatDate(filters.startDate),
    formattedEndDate: formatDate(filters.endDate),
    dimension: metricMapping.get(filters.activeMetric) || 'total',
    countries: (filters.countries ?? [])
      .filter(Boolean)
      .join(','),
    appID: (filters.appID ?? [])
      .filter(Boolean)
      .join(','),
    adUnitType: (filters.adUnitType ?? [])
      .filter(Boolean)
      .join(','),
    platform: (filters.platform ?? [])
      .filter(Boolean)
      .join(','),
    sp_id: (filters.homepageOrgId ?? []).filter(spId => spId.startsWith('SP')).join(','),
    dp_id: (filters.homepageOrgId ?? []).filter(dpId => dpId.startsWith('DP')).join(','),
  });
}

export async function getDashboardData(
  filters: DashboardFilters & { activeMetric: string },
  signal?: AbortSignal
) {
  return api.get<DashboardResponse>(buildDashboardQuery(filters), {
    headers: { 'X-Encrypted': 'true' },
    signal
  });
}

export const ReportsServices = {
  getAllReports(): Promise<IReportsResponse[]>{
    return api.get<IReportsResponse[]>(getAllReportsUri);
  },
  createAndSaveReport(data:ISaveReportRequest): Promise<{ reportId: string }> {
    return api.post(createAndSaveReportUri, data);
  },
  editAndSaveReport(reportId: string, data: ISaveReportRequest): Promise<{ reportId: string }> {
    return api.put(editAndSaveReportUri(reportId), data);
  },
  downloadReportById(reportId: string): Promise<string> {
    return api.get<string>(downloadReportByIdUri(reportId));
  },
  downloadTempReport(data: ISaveReportRequest): Promise<string> {
    return api.post<ISaveReportRequest, string>(downloadTempReportUri, data);
  },
  deleteReportById(reportId: string): Promise<void> {
    return api.delete(deleteReportByIdUri(reportId));
  }
};
